import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableRow,
  Typography
} from "@material-ui/core";
import styled from "styled-components";
import RoundedAvatar from "../../../waybee-ui/RoundedAvatar";
import StudentInfoPopover from "../../../components/StudentInfoPopover";

const TableCell = styled(MuiTableCell)`
  border-bottom: none;
  padding-top: 10px;
  padding-bottom: 10px;
`;

const CheckboxCell = styled(TableCell)`
  width: 52px;
  padding: 8px;
`;

const ClickableRow = styled(TableRow)`
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};

  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? "transparent" : "rgba(0, 0, 0, 0.04)"};
  }
`;

const DependentRow = styled(ClickableRow)`
  background-color: rgba(0, 0, 0, 0.02);
`;

const DependentCell = styled(TableCell)`
  position: relative;
  padding-left: 56px !important;

  &::before {
    content: "";
    position: absolute;
    left: 28px;
    top: 0;
    bottom: 50%;
    width: 2px;
    background: rgba(0, 0, 0, 0.12);
  }

  &::after {
    content: "";
    position: absolute;
    left: 28px;
    top: 50%;
    width: 16px;
    height: 2px;
    background: rgba(0, 0, 0, 0.12);
  }
`;

const UserInfo = styled(Box)`
  margin-left: 12px;
  min-width: 0;
`;

const TextName = styled(Typography)`
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ResponsibleReceiversSelector = ({
  users,
  disabledResponsible,
  disabledDependents,
  showStudentLabel,
  onToggleUser,
  onToggleDependent
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverUserId, setPopoverUserId] = useState(null);

  const popoverOpen = (event, id) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setPopoverUserId(id);
  };

  const popoverClose = () => {
    setAnchorEl(null);
    setPopoverUserId(null);
  };

  if (!users || users.length === 0) {
    return (
      <Box my={4} mx={2}>
        <Typography variant="body1" align="center">
          Nenhum responsável encontrado.
        </Typography>
      </Box>
    );
  }

  return (
    <Table aria-label="responsibles table">
      <TableBody>
        {users.map(responsible => (
          <React.Fragment key={responsible.id}>
            <ClickableRow
              $disabled={disabledResponsible}
              onClick={() => {
                if (!disabledResponsible) {
                  onToggleUser(responsible.id, !responsible.checked);
                }
              }}
            >
              <TableCell className="responsible">
                <Box display="flex" alignItems="center">
                  <RoundedAvatar
                    imageSrc={responsible.photoProfile}
                    icon="person"
                    size={40}
                  />
                  <UserInfo>
                    <TextName
                      variant="body2"
                      onClick={e => popoverOpen(e, responsible.id)}
                    >
                      {responsible.name}
                    </TextName>
                    <Typography variant="body2" color="textSecondary">
                      Responsável
                    </Typography>
                  </UserInfo>
                </Box>
              </TableCell>
              <CheckboxCell align="center">
                <Checkbox
                  checked={responsible.checked}
                  color="primary"
                  disableRipple
                  onClick={e => e.stopPropagation()}
                  onChange={event =>
                    onToggleUser(responsible.id, event.target.checked)
                  }
                  disabled={disabledResponsible}
                />
              </CheckboxCell>
            </ClickableRow>
            {(responsible.dependents || []).map(dependent => (
              <DependentRow
                key={`${responsible.id}-${dependent.id}`}
                $disabled={disabledDependents || !responsible.checked}
                onClick={() => {
                  if (!disabledDependents && responsible.checked) {
                    onToggleDependent(
                      responsible.id,
                      dependent.id,
                      !dependent.checked
                    );
                  }
                }}
              >
                <DependentCell>
                  <Box display="flex" alignItems="center">
                    <RoundedAvatar
                      imageSrc={dependent.photoProfile}
                      icon="person"
                      size={32}
                    />
                    <UserInfo>
                      <TextName
                        variant="body2"
                        onClick={e => popoverOpen(e, dependent.id)}
                      >
                        {dependent.name}
                      </TextName>
                      <Typography variant="body2" color="textSecondary">
                        {showStudentLabel && "Aluno · "}
                        RA:
                        {" "}
                        {dependent.studentId}
                      </Typography>
                    </UserInfo>
                  </Box>
                </DependentCell>
                <CheckboxCell align="center">
                  <Checkbox
                    checked={dependent.checked}
                    color="primary"
                    disableRipple
                    onClick={e => e.stopPropagation()}
                    onChange={event =>
                      onToggleDependent(
                        responsible.id,
                        dependent.id,
                        event.target.checked
                      )
                    }
                    disabled={disabledDependents || !responsible.checked}
                  />
                </CheckboxCell>
              </DependentRow>
            ))}
          </React.Fragment>
        ))}
        {popoverUserId && (
          <StudentInfoPopover
            anchorEl={anchorEl}
            onClose={popoverClose}
            userId={popoverUserId}
          />
        )}
      </TableBody>
    </Table>
  );
};

ResponsibleReceiversSelector.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      photoProfile: PropTypes.string,
      checked: PropTypes.bool,
      dependents: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
          photoProfile: PropTypes.string,
          studentId: PropTypes.string,
          checked: PropTypes.bool
        })
      )
    })
  ).isRequired,
  onToggleUser: PropTypes.func.isRequired,
  onToggleDependent: PropTypes.func.isRequired,
  disabledResponsible: PropTypes.bool,
  disabledDependents: PropTypes.bool,
  showStudentLabel: PropTypes.bool
};

ResponsibleReceiversSelector.defaultProps = {
  disabledResponsible: false,
  disabledDependents: false,
  showStudentLabel: false
};

export default ResponsibleReceiversSelector;
